import React, { useState } from 'react';
import { User, Lock, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import Button from '../../../components/ui/Button';
import LoadingSpinner from './LoadingSpinner';
import { useAuth } from '../../../contexts/AuthContext';

const LoginForm = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field error when user starts typing
    if (fieldErrors?.[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear global error when user starts typing
    if (error) {
      setError('');
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData?.username?.trim()) {
      errors.username = 'Il nome utente è obbligatorio';
    }
    
    if (!formData?.password?.trim()) {
      errors.password = 'La password è obbligatoria';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const result = await signIn(formData?.username, formData?.password);
      
      if (result?.success) {
        // Successful login - navigate to dashboard or home
        navigate('/product-order-management');
      } else {
        // Show error message
        setError(result?.error || 'Errore durante l\'accesso. Riprova.');
      }
    } catch (error) {
      setError('Errore di connessione. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <div className="w-5 h-5 text-red-400">⚠️</div>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form className="space-y-6" onSubmit={handleSubmit}>
        <div>
          <label 
            htmlFor="username" 
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Nome Utente
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="username"
              name="username"
              type="text"
              value={formData?.username}
              onChange={handleChange}
              className={`
                block w-full pl-10 pr-3 py-3 border rounded-md shadow-sm text-lg
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                ${fieldErrors?.username 
                  ? 'border-red-300 bg-red-50' :'border-gray-300 bg-white'
                }
              `}
              placeholder="Inserisci il nome utente"
              disabled={loading}
              autoComplete="username"
            />
          </div>
          {fieldErrors?.username && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors?.username}</p>
          )}
        </div>

        <div>
          <label 
            htmlFor="password" 
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData?.password}
              onChange={handleChange}
              className={`
                block w-full pl-10 pr-12 py-3 border rounded-md shadow-sm text-lg
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                ${fieldErrors?.password 
                  ? 'border-red-300 bg-red-50' :'border-gray-300 bg-white'
                }
              `}
              placeholder="Inserisci la password"
              disabled={loading}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              disabled={loading}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              ) : (
                <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
          {fieldErrors?.password && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors?.password}</p>
          )}
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        >
          {loading ? (
            <>
              <LoadingSpinner />
              <span className="ml-2">Accesso in corso...</span>
            </>
          ) : (
            'Accedi'
          )}
        </Button>
      </form>
    </div>
  );
};

export default LoginForm;
